/**
 * Brochure — a print-only, A4 multi-page reproduction of the entire landing
 * page. Hidden on screen (.brochure { display:none }) and revealed only when
 * printing / exporting to PDF (see @media print in index.css). It mirrors the
 * site's design language: Chivo / IBM Plex / JetBrains Mono fonts, the steel
 * engineering-blue accent (#5B9BD5), the warning orange (#FF5722), spec labels,
 * corner brackets and the technical grid — so the PDF reads as the same product.
 */

const oemFails = [
  { title: "Inclined main impeller shield", detail: "Big OD/ID ratio — low external force tips the shield axis. It gets stuck mid-stroke." },
  { title: "Undersized secondary pump", detail: "Sub-10mm piston pump is hyper-sensitive to debris. Weak return spring can't recover." },
  { title: "Main piston seizes", detail: "Limited design space means a weak spring. Once it sticks — coolant flow drops to zero." },
];

const aftermarketFails = [
  { title: "Collector cuts the flow", detail: "Turbulence under the main impeller. Cavitation at high RPM kills the secondary pump." },
  { title: "Shield function loss", detail: "Unbalanced spring vs. rubber-impeller pressure — the shield stops obeying." },
  { title: "Rubber impeller wear", detail: "Abrasive particles from the cooling system shred the vanes. Pressure collapses." },
];

const improvements = [
  {
    id: "01",
    title: "Impeller Back-face Vanes",
    short: "Vane-driven hydraulic boost.",
    description:
      "We removed the parasitic collector entirely. Custom back-face vanes accelerate the coolant tangentially before it hits the secondary-pump inlet, building meaningful pre-pressure without ever choking the main impeller.",
    image: "/assets/ewp1110_render6.png",
    badge: "PROPRIETARY DESIGN",
  },
  {
    id: "02",
    title: "Spring Retainer Inlet Passage",
    short: "Laminar feed geometry.",
    description:
      "Smooth inlet, no obstacles, no turbulence. Higher-velocity coolant enters the inner cavity cleanly.",
    image: "/assets/ewp1110_render5.png",
    badge: "FLOW-OPTIMIZED",
  },
  {
    id: "03",
    title: "Integrated Inlet Filter",
    short: "Debris stops at the gate.",
    description:
      "A purpose-engineered filter mesh in the inlet passage blocks abrasive particles before they reach the rubber impeller or by-pass passages. No clogs. No vane wear.",
    image: "/assets/ewp1110_render2.png",
    badge: "FAIL-SAFE ENABLER",
  },
  {
    id: "04",
    title: "Reinforced Return Spring",
    short: "Thicker wire, higher pre-load.",
    description:
      "We rewound the main piston return spring with a heavier wire gauge and a tuned rate so it always overcomes secondary pressure on shutdown — guaranteeing the open-position fail-safe.",
    image: "/assets/ewp1110_render3.png",
    badge: "OPEN-STATE BIAS",
  },
];

const compareRows = [
  { feature: "Failure State", oem: { v: "Closed", t: "bad", sub: "Engine overheats" }, mps: { v: "Open", t: "good", sub: "Full coolant flow" } },
  { feature: "Engineering Origin", oem: { v: "Legacy OE", t: "neutral", sub: "Original platform design" }, mps: { v: "Re-engineered", t: "good", sub: "Ground-up improvement" } },
  { feature: "Return Spring", oem: { v: "Standard", t: "bad", sub: "Under-sized" }, mps: { v: "Reinforced", t: "good", sub: "Thicker wire, higher pre-load" } },
  { feature: "Inlet Filter", oem: { v: "None", t: "bad", sub: "Debris ingress" }, mps: { v: "Integrated", t: "good", sub: "Protects secondary pump" } },
  { feature: "Secondary Pump Type", oem: { v: "Piston <10mm", t: "bad", sub: "Debris-sensitive" }, mps: { v: "Flexible impeller", t: "good", sub: "Filter-protected" } },
  { feature: "Flow Collector", oem: { v: "—", t: "neutral", sub: "N/A" }, mps: { v: "Eliminated", t: "good", sub: "Back-face vanes replace it" } },
  { feature: "Warranty Exposure", oem: { v: "High", t: "bad", sub: "Known failure pattern" }, mps: { v: "Minimal", t: "good", sub: "Engineered out" } },
];

const specs = [
  { k: "MPS Part No.", v: "MWP1110" },
  { k: "OE Reference", v: "04L 121 011" },
  { k: "Pump Type", v: "Switchable Coolant Pump" },
  { k: "Actuation", v: "Flexible-impeller hydraulic" },
  { k: "Failure Mode", v: "Locked Open" },
  { k: "Inlet Filter", v: "Integrated" },
  { k: "Return Spring", v: "Reinforced gauge" },
  { k: "Outer Diameter", v: "74.4 mm" },
  { k: "Total Length", v: "98.0 mm" },
  { k: "Impeller OD", v: "70.3 mm" },
  { k: "Design Origin", v: "MPS Proprietary" },
  { k: "Fitments", v: "VW · Audi · Škoda · SEAT · Cupra" },
];

const benefits = [
  { title: "Sell More, Return Less", body: "Workshops re-order from brands that don't come back. The MWP1110's fail-safe design slashes warranty exposure across your entire pump category.", metric: "↑ Reorder rate" },
  { title: "Proprietary Engineering", body: "Every critical failure point of the original and aftermarket designs has been engineered out at the geometry level — giving you a confidently differentiated SKU to stock.", metric: "Re-engineered from zero" },
  { title: "Happier End-Customers", body: "A driver whose car keeps cooling on a failed pump tells the story. A driver with a warped head writes a one-star review.", metric: "Story-worthy reliability" },
  { title: "One Part, Massive Coverage", body: "VW, Audi, Škoda, SEAT, Cupra — covering EA888 platforms across the most installed engine family in Europe. One SKU. Huge addressable demand.", metric: "VAG-wide fitment" },
];

const TOTAL = 6;

function SpecLabel({ children }) {
  return <span className="bp-speclabel">{children}</span>;
}

function PageChrome({ title, n, children }) {
  return (
    <section className="bpage">
      <header className="bp-head">
        <div className="bp-head-brand">
          <img src="/assets/mps-logo.png" alt="" className="bp-head-logo" />
          <span className="font-mono bp-micro">MPS · Multi Parts</span>
        </div>
        <span className="font-mono bp-micro">{title}</span>
      </header>

      <div className="bp-body">{children}</div>

      <footer className="bp-foot font-mono bp-micro">
        <span>multiparts.net</span>
        <span>MWP1110 · 04L 121 011</span>
        <span>{String(n).padStart(2, "0")} / {String(TOTAL).padStart(2, "0")}</span>
      </footer>
    </section>
  );
}

export default function Brochure() {
  return (
    <div className="brochure" aria-hidden="true">
      {/* ============ PAGE 1 — COVER ============ */}
      <section className="bpage bpage-cover">
        <div className="bp-cover-grid" />
        <div className="bp-cover-brackets">
          <span className="bp-bk bp-bk-tl" />
          <span className="bp-bk bp-bk-tr" />
          <span className="bp-bk bp-bk-bl" />
          <span className="bp-bk bp-bk-br" />
        </div>

        <div className="bp-cover-inner">
          <div className="bp-cover-top">
            <div className="bp-head-brand">
              <img src="/assets/mps-logo.png" alt="MPS Multi Parts" className="bp-cover-logo" />
              <span className="font-mono bp-micro">Engineered Precision · Jupiter, FL · USA</span>
            </div>
            <span className="font-mono bp-micro">Technical Product Brochure</span>
          </div>

          <div className="bp-cover-head">
            <SpecLabel>Fail-Safe Switchable Coolant Pump</SpecLabel>
            <h1 className="font-chivo bp-cover-title">MWP1110</h1>
            <div className="font-chivo bp-cover-sub">
              Engineered to <span className="bp-accent">never overheat.</span>
            </div>
            <p className="font-plex bp-cover-lede">
              A re-engineered switchable coolant pump for VW, Audi, Škoda, SEAT and Cupra EA888
              platforms — OE ref <span className="font-mono bp-ink">04L&nbsp;121&nbsp;011</span>.
              Proprietary flow geometry, a reinforced fail-safe spring and a debris-shielded
              secondary pump. If it ever fails, it locks <span className="bp-accent bp-bold">OPEN</span>.
              The engine keeps cooling. The customer keeps driving.
            </p>
          </div>

          <div className="bp-cover-hero">
            <img src="/assets/ewp1110_render4.png" alt="MWP1110 studio render" />
          </div>

          <div className="bp-cover-stats">
            {[
              { k: "4", v: "Critical Upgrades" },
              { k: "100%", v: "Fail-Safe Open" },
              { k: "0", v: "Flow Restrictions" },
            ].map((s) => (
              <div key={s.v} className="bp-stat">
                <span className="font-chivo bp-stat-k">{s.k}</span>
                <span className="font-mono bp-stat-v">{s.v}</span>
              </div>
            ))}
          </div>

          <div className="bp-cover-foot">
            <div className="bp-chips">
              {["Volkswagen", "Audi", "Škoda", "SEAT", "Cupra"].map((b) => (
                <span key={b} className="font-mono bp-chip">{b}</span>
              ))}
            </div>
            <span className="font-mono bp-micro">sales@multiparts.net · +1 561 748 1515</span>
          </div>
        </div>
      </section>

      {/* ============ PAGE 2 — THE PROBLEM ============ */}
      <PageChrome title="The Failure Pattern" n={2}>
        <SpecLabel>VAG Coolant Pump · The Failure Pattern</SpecLabel>
        <h2 className="font-chivo bp-h2">
          Every other pump fails the <span className="bp-warn">same way</span>.
        </h2>
        <p className="font-plex bp-lede">
          When their switching mechanism fails, they fail <span className="bp-ink bp-bold">closed</span>.
          Coolant stops circulating. Within minutes, the engine warps. Within hours, the warranty
          claim hits your desk.
        </p>

        <div className="bp-two">
          {[
            { tag: "OEM", subtitle: "OEM Wobble-Plate Coolant Pump", title: "Wobble-Plate Axial Piston Pump", image: "/assets/oem_pump_photo.webp", fails: oemFails },
            { tag: "Aftermarket", subtitle: "Aftermarket Switchable Coolant Pump", title: "Rubber-Impeller Shutter Pump", image: "/assets/aftermarket_pump_photo.jpg", fails: aftermarketFails },
          ].map((c) => (
            <div key={c.tag} className="bp-card bp-avoid">
              <div className="bp-card-img">
                <img src={c.image} alt={c.title} />
                <span className="font-mono bp-tag bp-tag-warn">{c.tag}</span>
              </div>
              <div className="bp-card-body">
                <div className="font-mono bp-micro bp-tertiary">{c.subtitle}</div>
                <div className="font-chivo bp-card-title">{c.title}</div>
                {c.fails.map((f) => (
                  <div key={f.title} className="bp-fail">
                    <span className="bp-fail-x">✕</span>
                    <div>
                      <div className="font-chivo bp-fail-title">{f.title}</div>
                      <p className="font-plex bp-fail-detail">{f.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PageChrome>

      {/* ============ PAGE 3 — FAIL-SAFE ============ */}
      <PageChrome title="Fail-Safe Design" n={3}>
        <SpecLabel>MWP1110 · Fail-Safe Coolant Pump Design</SpecLabel>
        <h2 className="font-chivo bp-h2">
          When a water pump fails, it usually fails <span className="bp-warn">closed</span>.
        </h2>
        <p className="font-plex bp-lede">
          The MPS MWP1110 inverts the entire failure model. A reinforced spring, a debris-shielded
          secondary pump and a redesigned impeller path conspire to do one thing — if ANY part of the
          switching mechanism fails, the shield retracts to the OPEN position. The pump returns to
          full unrestricted flow. The engine cools normally. The driver never knows there was a problem.
        </p>

        <div className="bp-two bp-avoid">
          <div className="bp-state bp-state-bad">
            <div className="font-mono bp-inline-tag bp-tag-warn">OEM &amp; Aftermarket</div>
            <div className="font-chivo bp-state-title">Fails <span className="bp-warn">CLOSED</span>.</div>
            <div className="bp-flowbar bp-flowbar-bad">◼ FLOW BLOCKED ◼</div>
            <ul className="font-plex bp-list">
              <li>Coolant flow stops at the shield.</li>
              <li>Cylinder head temperature spikes.</li>
              <li>Head gasket failure. Warped block.</li>
              <li>Warranty claim within 2,000 km.</li>
            </ul>
          </div>
          <div className="bp-state bp-state-good">
            <div className="font-mono bp-inline-tag bp-tag-accent">MPS MWP1110</div>
            <div className="font-chivo bp-state-title">Locks <span className="bp-accent">OPEN</span>.</div>
            <div className="bp-flowbar bp-flowbar-good">▸▸▸ 100% FLOW ▸▸▸</div>
            <ul className="font-plex bp-list">
              <li>Shield retracts to full-open position.</li>
              <li>100% coolant flow restored.</li>
              <li>Engine cools as if pump were brand new.</li>
              <li>Customer drives home. Zero claim.</li>
            </ul>
          </div>
        </div>

        <div className="bp-spring bp-avoid">
          <div className="bp-spring-img">
            <img src="/assets/ewp1110_render2.png" alt="MWP1110 cross-section, open flow path" />
          </div>
          <div className="bp-spring-copy">
            <div className="font-mono bp-micro bp-accent-text">Engineering Logic</div>
            <h3 className="font-chivo bp-h3">The reinforced return spring that keeps VAG engines safe.</h3>
            <p className="font-plex bp-body-text">
              The original VAG return spring is undersized — it cannot recover the main piston once
              contamination intrudes. MPS engineers thickened the spring wire and tuned the rate to a
              higher pre-load. Combined with the integrated inlet filter that stops abrasive debris
              from ever reaching the secondary pump, the MWP1110 is built so the spring
              <span className="bp-ink bp-bold"> always wins</span>.
            </p>
            <div className="bp-chips">
              {["Reinforced wire gauge", "Higher pre-load", "Filter-protected", "Open-state bias"].map((t) => (
                <span key={t} className="font-mono bp-chip">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </PageChrome>

      {/* ============ PAGE 4 — ENGINEERING ============ */}
      <PageChrome title="The Engineering" n={4}>
        <SpecLabel>Four Improvements · One Failure Mode Eliminated</SpecLabel>
        <h2 className="font-chivo bp-h2">
          The MPS MWP1110 solution, redesigned in <span className="bp-accent">four key improvements</span>.
        </h2>
        <p className="font-plex bp-lede">
          Each improvement targets a documented OEM or aftermarket failure mode. Together they form a
          switching pump that performs better — and fails better — than anything else on the market.
        </p>

        <div className="bp-eng-grid">
          {improvements.map((it) => (
            <div key={it.id} className="bp-eng-card bp-avoid">
              <div className="bp-eng-img">
                <img src={it.image} alt={it.title} />
              </div>
              <div className="bp-eng-copy">
                <div className="bp-eng-top">
                  <span className="font-mono bp-eng-id">{it.id} / 04</span>
                  <span className="font-mono bp-eng-badge">{it.badge}</span>
                </div>
                <div className="font-mono bp-eng-short">{it.short}</div>
                <h3 className="font-chivo bp-eng-title">{it.title}</h3>
                <p className="font-plex bp-eng-desc">{it.description}</p>
              </div>
            </div>
          ))}
        </div>
      </PageChrome>

      {/* ============ PAGE 5 — COMPARE + DISTRIBUTOR ============ */}
      <PageChrome title="Comparison & Distribution" n={5}>
        <SpecLabel>Comparison Matrix</SpecLabel>
        <h2 className="font-chivo bp-h2">
          MPS MWP1110 vs. OEM Coolant Pump — <span className="bp-accent">side by side</span>.
        </h2>

        <table className="bp-table bp-avoid">
          <thead>
            <tr>
              <th className="font-mono bp-th">Specification</th>
              <th className="font-mono bp-th bp-th-warn">OEM · Original</th>
              <th className="font-mono bp-th bp-th-accent">MPS MWP1110 · Engineered</th>
            </tr>
          </thead>
          <tbody>
            {compareRows.map((r, i) => (
              <tr key={r.feature} className={i % 2 ? "bp-tr-alt" : ""}>
                <td className="font-chivo bp-td-feat">{r.feature}</td>
                <td className="bp-td">
                  <span className={`font-chivo bp-val ${r.oem.t === "bad" ? "bp-ink" : "bp-secondary"}`}>
                    {r.oem.t === "bad" ? "✕ " : "– "}{r.oem.v}
                  </span>
                  <span className="font-plex bp-val-sub">{r.oem.sub}</span>
                </td>
                <td className="bp-td bp-td-accent">
                  <span className="font-chivo bp-val bp-accent">✓ {r.mps.v}</span>
                  <span className="font-plex bp-val-sub">{r.mps.sub}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="bp-dist-head">
          <SpecLabel>For VAG Aftermarket Parts Distributors</SpecLabel>
          <h3 className="font-chivo bp-h3">Stock the VAG coolant pump that <span className="bp-accent">sells itself.</span></h3>
        </div>
        <div className="bp-bene-grid bp-avoid">
          {benefits.map((b, i) => (
            <div key={b.title} className="bp-bene">
              <div className="bp-bene-top">
                <span className="font-chivo bp-bene-title">{b.title}</span>
                <span className="font-mono bp-eng-id">0{i + 1}</span>
              </div>
              <p className="font-plex bp-bene-body">{b.body}</p>
              <div className="font-mono bp-bene-metric">▪ {b.metric}</div>
            </div>
          ))}
        </div>
      </PageChrome>

      {/* ============ PAGE 6 — SPECS + CTA ============ */}
      <PageChrome title="Technical Data & Contact" n={6}>
        <SpecLabel>MWP1110 Technical Datasheet</SpecLabel>
        <h2 className="font-chivo bp-h2">
          MWP1110 specifications — <span className="bp-accent">technical data</span>.
        </h2>

        <div className="bp-spec-row bp-avoid">
          <div className="bp-spec-render">
            <div className="bp-cover-brackets bp-spec-bk">
              <span className="bp-bk bp-bk-tl" /><span className="bp-bk bp-bk-tr" />
              <span className="bp-bk bp-bk-bl" /><span className="bp-bk bp-bk-br" />
            </div>
            <img src="/assets/ewp1110_render4.png" alt="MWP1110 studio render" />
            <div className="font-mono bp-micro bp-tertiary bp-spec-cap">MPS-MWP1110 · STUDIO RENDER</div>
          </div>
          <div className="bp-spec-table">
            {specs.map((s) => (
              <div key={s.k} className="bp-spec-line">
                <span className="font-mono bp-spec-k">{s.k}</span>
                <span className="font-chivo bp-spec-v">{s.v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bp-cta bp-avoid">
          <SpecLabel>Become an MPS MWP1110 Distributor</SpecLabel>
          <h3 className="font-chivo bp-cta-title">
            Upgrade your VAG <span className="bp-accent">aftermarket catalog.</span>
          </h3>
          <p className="font-plex bp-body-text">
            Stock the MPS MWP1110 — the only switchable coolant pump for VAG that fails open. Talk to
            our team about distribution terms, MOQs and territory exclusivity.
          </p>
          <div className="bp-contact">
            <div className="bp-contact-card">
              <div className="font-mono bp-micro bp-tertiary">Email</div>
              <div className="font-chivo bp-contact-v">sales@multiparts.net</div>
            </div>
            <div className="bp-contact-card">
              <div className="font-mono bp-micro bp-tertiary">Phone</div>
              <div className="font-chivo bp-contact-v">+1 561 748 1515</div>
            </div>
            <div className="bp-contact-card">
              <div className="font-mono bp-micro bp-tertiary">HQ</div>
              <div className="font-chivo bp-contact-v">Jupiter, FL · USA</div>
            </div>
          </div>
        </div>
      </PageChrome>
    </div>
  );
}
